import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import type { DS2ModalActionVariant } from '../components/ui/DS2Modal';

/**
 * ConfirmContext — the app-wide confirmation service. Mount `ConfirmProvider`
 * once near the root. One `ConfirmModal` (canonical DS2Modal shell) is
 * rendered for all callers.
 *
 * Two consumer hooks share the same modal:
 *   • useConfirm()       → Promise<boolean>            (2-button yes/no)
 *   • useConfirmChoice() → Promise<ConfirmResult>      (adds an optional 3rd
 *     "tertiary" action, e.g. Save-for-later / End-game / Stay)
 *
 * Standard confirmation primitive for the whole app: leave game, delete friend,
 * delete account, kick player, cancel invitation, etc.
 *
 * An action given a `*Run` callback executes that async work INSIDE the modal:
 * pending label, all actions disabled, dismissal blocked; on failure the modal
 * stays open with the inline error and the promise stays unresolved (the user
 * can retry or cancel). The promise resolves with that action only after the
 * run has succeeded.
 *
 * CONCURRENCY: requests are queued FIFO. A request that arrives while another
 * dialog is visible (idle OR mid-run) waits its turn — the active request's
 * resolver, options, and pending API action are NEVER replaced, no promise is
 * ever orphaned, and only one dialog is visible at a time. When the current
 * request fully resolves, the next queued request is presented through the
 * same mounted shell (fresh interaction state via requestKey), so the
 * backdrop, scroll lock, and inert background never flicker between
 * consecutive dialogs.
 */

export type ConfirmResult = 'confirm' | 'cancel' | 'tertiary';

export interface ConfirmOptions {
  title:            string;
  message:          string;
  confirmLabel?:    string;
  cancelLabel?:     string;
  variant?:         'default' | 'danger';
  /** Optional visual slot rendered above the title. */
  icon?:            ReactNode;
  /** Optional third action; presence turns the dialog into a 3-choice modal. */
  tertiaryLabel?:   string;
  tertiaryVariant?: 'default' | 'danger' | DS2ModalActionVariant;
  /** Explicit DS2 action variants (gold/green/red/neutral) — override `variant` defaults. */
  confirmVariant?:  DS2ModalActionVariant;
  cancelVariant?:   DS2ModalActionVariant;
  /** Async work run inside the modal before the action resolves (see above). */
  confirmRun?:      () => Promise<void>;
  tertiaryRun?:     () => Promise<void>;
  confirmPendingLabel?:  string;
  tertiaryPendingLabel?: string;
  /** Error message when a run rejects; the shell supplies a default. */
  errorText?:       string;
  /** Escape/backdrop dismiss resolves 'cancel'. Default true. */
  dismissible?:     boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
type ChoiceFn  = (options: ConfirmOptions) => Promise<ConfirmResult>;

interface ContextValue {
  confirm:       ConfirmFn;
  confirmChoice: ChoiceFn;
}

const ConfirmContext = createContext<ContextValue | null>(null);

interface PendingConfirm {
  /** Monotonic identity — drives DS2Modal's per-request state reset. */
  key: number;
  options: ConfirmOptions;
  resolve: (result: ConfirmResult) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  // `current` mirrors currentRef for rendering; the refs are the source of
  // truth so request/settle stay side-effect-free inside React state updaters
  // (StrictMode double-invokes updaters — resolving promises or mutating the
  // queue there would duplicate).
  const [current, setCurrent] = useState<PendingConfirm | null>(null);
  const currentRef = useRef<PendingConfirm | null>(null);
  const queueRef   = useRef<PendingConfirm[]>([]);
  const seqRef     = useRef(0);

  // Core request — FIFO. If a dialog is already visible the new request waits;
  // the active request is never replaced and no promise is orphaned.
  const request = useCallback((options: ConfirmOptions): Promise<ConfirmResult> => {
    return new Promise<ConfirmResult>((resolve) => {
      const item: PendingConfirm = { key: ++seqRef.current, options, resolve };
      if (currentRef.current) {
        queueRef.current.push(item);
      } else {
        currentRef.current = item;
        setCurrent(item);
      }
    });
  }, []);

  const confirm       = useCallback<ConfirmFn>((o) => request(o).then((r) => r === 'confirm'), [request]);
  const confirmChoice = useCallback<ChoiceFn>((o) => request(o), [request]);

  // Resolve the current request, then advance to the next queued one (if any)
  // in the same commit — the shell stays mounted, so no backdrop flicker.
  const settle = useCallback((result: ConfirmResult) => {
    const cur = currentRef.current;
    if (!cur) return;
    cur.resolve(result);
    const next = queueRef.current.shift() ?? null;
    currentRef.current = next;
    setCurrent(next);
  }, []);

  // Keep the last request's options renderable while DS2Modal plays its exit
  // transition after the final queued dialog resolves (open=false unmounts
  // the shell only once the exit completes).
  const lastRef = useRef<PendingConfirm | null>(null);
  if (current) lastRef.current = current;
  const display = current ?? lastRef.current;

  return (
    <ConfirmContext.Provider value={{ confirm, confirmChoice }}>
      {children}
      {display && (
        <ConfirmModal
          open={current !== null}
          requestKey={display.key}
          title={display.options.title}
          message={display.options.message}
          confirmLabel={display.options.confirmLabel}
          cancelLabel={display.options.cancelLabel}
          variant={display.options.variant}
          confirmVariant={display.options.confirmVariant}
          cancelVariant={display.options.cancelVariant}
          icon={display.options.icon}
          tertiaryLabel={display.options.tertiaryLabel}
          tertiaryVariant={display.options.tertiaryVariant}
          confirmRun={display.options.confirmRun}
          tertiaryRun={display.options.tertiaryRun}
          confirmPendingLabel={display.options.confirmPendingLabel}
          tertiaryPendingLabel={display.options.tertiaryPendingLabel}
          errorText={display.options.errorText}
          dismissible={display.options.dismissible}
          onConfirm={() => settle('confirm')}
          onCancel={() => settle('cancel')}
          onTertiary={() => settle('tertiary')}
        />
      )}
    </ConfirmContext.Provider>
  );
}

/** 2-button yes/no. Throws if used outside the provider. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx.confirm;
}

/** 3-choice (confirm / tertiary / cancel). Throws if used outside the provider. */
export function useConfirmChoice(): ChoiceFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirmChoice must be used within a ConfirmProvider');
  return ctx.confirmChoice;
}
